import React, { useCallback, useContext, useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { AppContext } from '../../App';
import { useDirectLabelPrint } from '../../hooks/useDirectLabelPrint';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';
import LabelPreviewGrid from './LabelPreviewGrid';
import PrinterSelectionModal from './PrinterSelectionModal';
import PrinterSetupHelp from './PrinterSetupHelp';
import './PrintLabelsModal.css';

const LOG_CONTEXT = 'PrintLabelsModal';

/**
 * Order-detail label printing: QZ Tray direct print (default) + browser preview fallback.
 */
const PrintLabelsModal = ({ orderId, disabled = false }) => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const {
    busy,
    fetchLabels,
    resolvePrinter,
    printLabels,
    getSavedPrinterName,
  } = useDirectLabelPrint(token);

  const [show, setShow] = useState(false);
  const [mode, setMode] = useState('confirm');
  const [loading, setLoading] = useState(false);
  const [labels, setLabels] = useState([]);
  const [orderLabelId, setOrderLabelId] = useState(null);
  const [printerName, setPrinterName] = useState(getSavedPrinterName());
  const [error, setError] = useState('');
  const [errorActions, setErrorActions] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  const resetState = () => {
    setMode('confirm');
    setLabels([]);
    setOrderLabelId(null);
    setError('');
    setErrorActions({});
    setSuccessMessage('');
  };

  const handleClose = () => {
    setShow(false);
    resetState();
  };

  const loadLabelsAndOpen = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setError('');
    const result = await fetchLabels(orderId);
    setLoading(false);
    if (!result.success) {
      showToast(result.message, 'error', 'top-right', 5000);
      return;
    }
    setLabels(result.labels);
    setOrderLabelId(result.orderId);
    try {
      const resolved = await resolvePrinter();
      setPrinterName(resolved.printerName || getSavedPrinterName());
      if (resolved.needsSelection && !resolved.printerName) {
        setShowPrinterModal(true);
      }
    } catch {
      // QZ unavailable — user can still preview
    }
    setShow(true);
    logInfo(LOG_CONTEXT, 'confirmation opened', {
      orderId: result.orderId,
      count: result.labels.length,
    });
  }, [orderId, fetchLabels, resolvePrinter, getSavedPrinterName]);

  const handleDirectPrint = async () => {
    if (!labels.length || !orderLabelId) return;
    setError('');
    setSuccessMessage('');
    let targetPrinter = printerName || getSavedPrinterName();
    if (!targetPrinter) {
      setShowPrinterModal(true);
      return;
    }
    const result = await printLabels(orderLabelId, labels, targetPrinter);
    if (result.success) {
      setSuccessMessage(result.message);
      setPrinterName(result.printerName);
      showToast(result.message, 'success', 'top-right', 4000);
      return;
    }
    setError(result.message);
    setErrorActions({
      needsPreview: result.needsPreview,
      needsPrinterSelection: result.needsPrinterSelection,
      needsRetry: true,
    });
  };

  const handlePreview = () => {
    setMode('preview');
    setError('');
    logInfo(LOG_CONTEXT, 'preview mode', { orderId: orderLabelId, count: labels.length });
  };

  const handleBrowserPrint = () => {
    logInfo(LOG_CONTEXT, 'browser print', { orderId: orderLabelId, count: labels.length });
    window.print();
  };

  useEffect(() => {
    if (mode !== 'preview') return undefined;
    const onAfterPrint = () =>
      logInfo(LOG_CONTEXT, 'browser print closed', { orderId: orderLabelId });
    window.addEventListener('afterprint', onAfterPrint);
    return () => window.removeEventListener('afterprint', onAfterPrint);
  }, [mode, orderLabelId]);

  const labelCount = labels.length;
  const displayPrinter = printerName || getSavedPrinterName() || 'Not configured';

  if (isViewer) {
    return null;
  }

  return (
    <>
      <Button
        variant="outline-primary"
        className="me-2 print-labels-trigger"
        disabled={disabled || loading || busy || !orderId}
        onClick={loadLabelsAndOpen}
      >
        {loading || busy ? (
          <>
            <Spinner animation="border" size="sm" className="me-1" role="status" />
            {loading ? 'Loading…' : 'Printing…'}
          </>
        ) : (
          'Print QR labels'
        )}
      </Button>

      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        className="print-labels-modal"
        dialogClassName="print-labels-modal__dialog"
      >
        <Modal.Header closeButton className="print-labels-modal__header">
          <Modal.Title>
            {mode === 'preview'
              ? `Label preview${orderLabelId ? ` — Order #${orderLabelId}` : ''}`
              : `Print QR labels${orderLabelId ? ` — Order #${orderLabelId}` : ''}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="print-labels-print-area">
          {mode === 'confirm' ? (
            <>
              <PrinterSetupHelp orderId={orderLabelId} labels={labels} />
              <p className="mb-2">
                <strong>{labelCount}</strong> stock-item{' '}
                {labelCount === 1 ? 'label is' : 'labels are'} ready.
              </p>
              <p className="text-muted mb-3">
                Printer: <strong>{displayPrinter}</strong>
              </p>
              {successMessage ? (
                <Alert variant="success">{successMessage}</Alert>
              ) : null}
              {error ? (
                <Alert variant="danger" className="print-labels-modal__error">
                  <div style={{ whiteSpace: 'pre-line' }}>{error}</div>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {errorActions.needsRetry ? (
                      <Button size="sm" variant="outline-primary" onClick={handleDirectPrint}>
                        Try again
                      </Button>
                    ) : null}
                    {errorActions.needsPrinterSelection ? (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => setShowPrinterModal(true)}
                      >
                        Select printer
                      </Button>
                    ) : null}
                    {errorActions.needsPreview ? (
                      <Button size="sm" variant="outline-secondary" onClick={handlePreview}>
                        Print preview
                      </Button>
                    ) : null}
                  </div>
                </Alert>
              ) : null}
            </>
          ) : (
            <LabelPreviewGrid labels={labels} />
          )}
        </Modal.Body>
        <Modal.Footer className="print-labels-modal__footer">
          {mode === 'confirm' ? (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="outline-secondary" onClick={() => setShowPrinterModal(true)}>
                Change printer
              </Button>
              <Button variant="outline-secondary" onClick={handlePreview}>
                Print preview
              </Button>
              <Button variant="primary" onClick={handleDirectPrint} disabled={busy || !labelCount}>
                {busy ? 'Printing…' : `Print ${labelCount} label${labelCount === 1 ? '' : 's'}`}
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setMode('confirm')}>
                Back
              </Button>
              <Button variant="primary" onClick={handleBrowserPrint}>
                Print
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <PrinterSelectionModal
        show={showPrinterModal}
        onHide={() => setShowPrinterModal(false)}
        authToken={token}
        initialSelection={printerName || getSavedPrinterName() || ''}
        onSaved={name => {
          setPrinterName(name);
          setError('');
        }}
      />
    </>
  );
};

export default PrintLabelsModal;
